import { useState, useEffect } from "react"
import { bool, number } from "prop-types"
import API from "flux/api/api"
import { useSettingPane } from "components/admin/legacySettingPanes"
import { Pane } from "components/ui"
import { Button } from "@planningcenter/tapestry-react"
import BlankStateCampusCategory from "./BlankStateCampusCategory"
import Campuses from "./Campuses"
import Categories from "./Categories"

const mapDataAttributes = data =>
  data.map(d => ({
    id: d.id,
    name: d.attributes.name,
    action: null
  }))

const dataToSave = data => data.filter(d => d.action !== null)

const CampusCategoryPane = ({
  eventId,
  isOrgAdmin,
  isAdmin,
  orgHasCampuses,
  orgHasCategories: orgHasCategoriesFromProps,
  eventIsArchived
}) => {
  const [selectedCampuses, setSelectedCampuses] = useState([])
  const [selectedCategories, setSelectedCategories] = useState([])
  const [orgHasCategories, setOrgHasCategories] = useState(orgHasCategoriesFromProps || false)

  const {
    isEditing,
    isLoading,
    status,
    handleIsEditing,
    handleSave,
    handleFetch,
    handleCancel,
    data,
    handleDataChange
  } = useSettingPane({
    isLoading: true,
    data: {
      campuses: [],
      categories: [],
      eventCampuses: [],
      eventCategories: []
    }
  })

  useEffect(() => {
    setSelectedCategories(mapDataAttributes(data.eventCategories))
  }, [data.eventCategories])

  useEffect(() => {
    setSelectedCampuses(mapDataAttributes(data.eventCampuses))
  }, [data.eventCampuses])

  useEffect(() => {
    const fetchData = async () => {
      await handleFetch({
        campuses: API.getAll("/campuses?order=name", { perPage: 100 }),
        eventCampuses: API.getAll(`/events/${eventId}/campuses?order=name`, {
          perPage: 100
        }),
        categories: API.getAll("/categories?order=name", { perPage: 100 }),
        eventCategories: API.getAll(`/events/${eventId}/categories?order=name`, {
          perPage: 100
        })
      })
    }

    fetchData()
  }, [])

  const handleCampusCategoryCancel = () => {
    setSelectedCategories(mapDataAttributes(data.eventCategories))
    setSelectedCampuses(mapDataAttributes(data.eventCampuses))
    handleCancel()
  }

  const handleCampusChange = items => setSelectedCampuses(items)

  const handleCategoryChange = items => {
    setSelectedCategories(items)
  }

  const handleCampusCategorySave = async () => {
    const categoriesToSave = dataToSave(selectedCategories)
    const campusesToSave = dataToSave(selectedCampuses)

    const { eventCategories, eventCampuses } = await handleSave({
      ...(categoriesToSave.length > 0 && {
        eventCategories: categoriesToSave.map(item =>
          API.post(`/events/${eventId}/${item.action}`, {
            attributes: {},
            relationships: { categories: { data: [{ id: item.id }] } }
          })
        )
      }),
      ...(campusesToSave.length > 0 && {
        eventCampuses: campusesToSave.map(item =>
          API.post(`/events/${eventId}/${item.action}`, {
            attributes: {},
            relationships: { campuses: { data: [{ id: item.id }] } }
          })
        )
      })
    })

    eventCategories &&
      !eventCategories.hasError &&
      setSelectedCategories(mapDataAttributes(eventCategories.data))

    eventCampuses &&
      !eventCampuses.hasError &&
      setSelectedCampuses(mapDataAttributes(eventCampuses.data))
  }

  const handleModalChange = response => {
    setSelectedCategories(
      selectedCategories.filter(cts => response.data.map(r => r.id).includes(cts.id))
    )

    handleDataChange({ categories: response.data })
  }

  const handleModalClose = onClose => {
    onClose()
    handleIsEditing(data.categories.length > 0 ? true : false)
    setOrgHasCategories(data.categories.length > 0 ? true : false)
  }

  const useOrgMissingBlankState = !orgHasCampuses && !orgHasCategories

  const useEventMissingBlankState =
    data.eventCampuses.length === 0 && data.eventCategories.length === 0 && !isEditing

  const isChangePending =
    [...dataToSave(selectedCampuses), ...dataToSave(selectedCategories)].length > 0

  const hasRecords = selectedCampuses.length > 0 || selectedCategories.length > 0

  let paneStatus = !status && isChangePending ? "pending" : status

  return (
    <Pane>
      <Pane.Header>
        <Pane.Title>Campuses & Categories</Pane.Title>
        {!isEditing && !isLoading && (
          <Button
            title={hasRecords ? "Edit" : "Add category"}
            onClick={() => handleIsEditing(true)}
            size="xs"
            variant="outline"
          />
        )}
        {isEditing && !isLoading && (
          <div className="d-f ai-c g-1">
            <Button
              disabled={paneStatus === "saving"}
              onClick={handleCampusCategoryCancel}
              size="xs"
              variant="outline"
              title="Cancel"
            />
            <Button
              disabled={paneStatus !== "pending" || paneStatus === "saving"}
              onClick={handleCampusCategorySave}
              size="xs"
              theme="create"
              title="Save"
            />
          </div>
        )}
      </Pane.Header>
      <Pane.Body>
        {isEditing || hasRecords ? (
          <>
            <Categories
              isEditing={isEditing}
              categories={data.categories}
              eventIsArchived={eventIsArchived}
              onCategoryChange={handleCategoryChange}
              onModalChange={handleModalChange}
              onModalClose={handleModalClose}
              selectedCategories={selectedCategories}
              isAdmin={isAdmin}
            />
            <Campuses
              isEditing={isEditing}
              campuses={data.campuses}
              onCampusChange={handleCampusChange}
              selectedCampuses={selectedCampuses}
            />
          </>
        ) : (
          <BlankStateCampusCategory
            useOrgMissingBlankState={useOrgMissingBlankState}
            useEventMissingBlankState={useEventMissingBlankState}
            isOrgAdmin={isOrgAdmin}
            onIsEditing={handleIsEditing}
            onModalChange={handleModalChange}
            onModalClose={handleModalClose}
            isAdmin={isAdmin}
          />
        )}
      </Pane.Body>
    </Pane>
  )
}

CampusCategoryPane.propTypes = {
  eventId: number,
  isOrgAdmin: bool,
  orgHasCampuses: bool,
  orgHasCategories: bool,
  eventIsArchived: bool,
  isAdmin: bool
}

export default CampusCategoryPane
